// Libraries - using global gtag function loaded in HTML

// Constants
import { GoogleAnalyticsCode } from '../Constants'
import { isDev } from '../utils/DebugUtils'

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

let isInit = false
let currentUserId: string | null = null
const measurementId = GoogleAnalyticsCode

// Which client the person came from. The widget serves every product — the
// web client, the mobile app's auth session, the native webview, the custom
// skin builder — and without this no auth event can be split by client.
// Attached to every event by logEvent, set once from the `type` query param.
export type AuthOrigin = 'web' | 'mobile' | 'native' | 'csb' | 'extension'
let currentOrigin: AuthOrigin = 'web'

/**
 * @description set the originating client from the widget's `type` param.
 * mobile and native are the app's two auth paths; csb is the custom skin
 * builder, its own product; extension is Chrome/browser extensions;
 * everything else (web, iframe, popup, absent) is web.
 **/
export function setOrigin(type?: string | null) {
  const next: AuthOrigin =
    type === 'mobile' || type === 'native' || type === 'csb' || type === 'extension'
      ? type
      : 'web'
  if (next !== currentOrigin && isDev()) {
    console.info('GoogleAnalyticsService: setOrigin', next)
  }
  currentOrigin = next
}

function syncUserIdToGa() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_id', currentUserId ?? null)
  }
}

function syncAuthStateProperty() {
  if (typeof window !== 'undefined' && window.gtag && measurementId) {
    window.gtag('config', measurementId, {
      user_properties: {
        is_authenticated: currentUserId ? 'true' : 'false',
      },
    })
  }
}

export function init() {
  if (isInit) {
    return;
  }

  if (!measurementId) {
    console.warn('Google Analytics measurement ID not found')
    return
  }

  // Check if gtag is available
  if (typeof window !== 'undefined' && window.gtag) {
    isInit = true
    console.log("GoogleAnalyticsService initialized with ID:", measurementId)
  } else {
    console.warn('gtag function not available')
  }
}

export function logout() {
  currentUserId = null
  syncUserIdToGa()
  syncAuthStateProperty()
  isInit = false
}

/**
 * @description set user id for Google Analytics
 * @param {string | null} userId - current app user id
 **/
export function setUserId(userId: string | null) {
  init()

  if (isDev()) {
    console.info('GoogleAnalyticsService: setUserId', userId)
  }

  currentUserId = userId
  syncUserIdToGa()
  syncAuthStateProperty()
}

/**
 * @description log event to Google Analytics
 * @param {string} name - event name
 * @param {any} properties - event properties
 **/
export async function logEvent(name: string, properties: any = {}): Promise<void> {
  init()

  isDev() && console.log(
    'Google Analytics Service: log event: ',
    name,
    JSON.stringify(properties, null, '2')
  )

  try {
    // GA4 rejects characters outside [A-Za-z0-9_] and caps event names at 40,
    // truncating silently past that — so the name in GA stops matching the name
    // in code and searching for the declared name returns nothing. Normalising
    // here makes that failure mode impossible to reintroduce. This is a no-op
    // for every name currently sent: all are already lowercase, underscore-only
    // and well under 40 characters.
    const eventName = name
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .toLowerCase()
      .substring(0, 40)

    if (isDev() && name.length > 40) {
      console.warn(
        `Google Analytics Service: event name is ${name.length} chars, over GA4's 40 limit. ` +
          `Sent as "${eventName}" — rename it to under 40 characters.`
      )
    }

    if (typeof window !== 'undefined' && window.gtag) {
      const eventParameters: any = {
        ...properties,
        event_category: 'engagement',
        // Deliberately the raw name, not the normalised one. If an over-length
        // name is ever introduced, the event fires under the truncated name
        // while this preserves the full intended one, making the mismatch
        // visible in GA itself rather than only in a dev console.
        event_label: name,
        is_authenticated: currentUserId ? 'true' : 'false',
        origin: currentOrigin,
      }

      if (currentUserId) {
        eventParameters.user_id = currentUserId
      }

      window.gtag('event', eventName, eventParameters)
    }
    return new Promise((resolve) => {
      setTimeout(resolve, 100);
    })
  } catch (e) {
    isDev() && console.error('Google Analytics Service: error sending log event: ', e)
    return Promise.resolve();
  }
}
